import { guardarTokenEquipanteEvento, obterTokenEquipanteEvento } from "@/lib/equipante-evento-acesso";
import { IAvaliacao, IChangeQuarto } from "@/interfaces/change.interface";
import {
  IChangeEquipePai,
  IEquipanteActionRequest,
  IEquipantesDataTableRequest,
  IListagemGeralPaginatedRequest,
  IToggleStatusEquipanteEvento,
} from "@/interfaces/equipe.interface";
import { IEventoPaginatedRequest } from "@/interfaces/paginated.interface";
import { IRemoverDuplicadoDto } from "@/interfaces/remover-duplicado.interface";
import { equipesModel } from "@/interfaces/zod";
import { downloadFile, normalizeTableDataWithHash, flattenEquipantesData } from "@/lib/utils";
import { skipGlobalErrorToast } from "@/lib/rtk-query-config";
import type { FetchBaseQueryError, QueryReturnValue } from "@reduxjs/toolkit/query/react";
import { setEquipante } from "@/redux/slices/auth.slice";
import type { RootState } from "@/redux/store";
import { z } from "zod";
import { createBaseApi, getBaseApiEndpointObjects } from "./base.service";
import { RESOURCES } from "./resources";

export type SugestaoConjuge = {
  Id: number;
  Nome: string | null;
  Apelido: string | null;
  Email: string | null;
  Fone: string | null;
  Sexo: number | null;
  Tipo: "Voluntário";
  EventoIdOrigem: number;
  NomeEventoOrigem: string | null;
  jaNoEvento: boolean;
  equipeAtual: { id: number; nome: string } | null;
};

export type ParInvalidoMontagem = {
  a: { equipanteId: number; nome: string | null; sexo: number | null };
  b: { equipanteId: number; nome: string | null; sexo: number | null };
  motivo: string;
};

type MontagemAssistenteBuscaVoluntarioItem = {
  Id: number;
  Nome: string | null;
  Apelido: string | null;
  Email: string | null;
  Fone: string | null;
  Tipo: string;
  jaNoEvento?: boolean;
  equipeAtual?: { id: number; nome: string };
};

export const equipeApi = createBaseApi<z.infer<typeof equipesModel>>(
  RESOURCES.EQUIPES
);

export const equipesAPI = equipeApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    postBaseEquipes: build.mutation<
      any,
      Partial<z.infer<typeof equipesModel>> & { skipInvalidatesTags?: boolean }
    >({
      query: (newData) => {
        const { skipInvalidatesTags: _skipInvalidatesTags, ...body } = newData;
        return {
          url: `/equipes`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, newData) => {
        if (newData?.skipInvalidatesTags) return [];
        return [
          { type: RESOURCES.EQUIPES, id: "LIST" },
          { type: "Equipes" },
          ...(newData?.Id ? [{ type: RESOURCES.EQUIPES, id: newData.Id }] : []),
          { type: "EquipesEvento", id: window.localStorage.getItem("EventoId") || "LIST" },
        ];
      },
    }),
    deleteBaseEquipes: build.mutation<void, number | string>({
      query: (id) => ({
        url: `/equipes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: RESOURCES.EQUIPES, id },
        { type: RESOURCES.EQUIPES, id: "LIST" },
        { type: "Equipes" }, // Invalidar também a tag "Equipes" para atualizar getEquipes
        { type: "EquipesEvento", id: window.localStorage.getItem("EventoId") || "LIST" },
      ],
    }),
    getEquipes: build.query({
      query: () => ({
        url: `/equipes`,
        method: "GET",
      }),
      providesTags: () => [
        { type: "Equipes" },
        { type: RESOURCES.EQUIPES, id: "LIST" },
      ],
    }),
    postEquipe: build.mutation<any, z.infer<typeof equipesModel>>({
      query: (body) => ({
        url: `/equipes`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "Equipes" },
        { type: RESOURCES.EQUIPES, id: "LIST" },
      ],
    }),
    postInscricaoEquipe: build.mutation<any, any>({
      query: (body: any) => ({
        url: `/equipes/inscricao`,
        method: "POST",
        body,
      }),
      transformResponse: (response: any) => {
        for (const e of response?.equipanteeventos ?? []) guardarTokenEquipanteEvento(e.Id, e.TokenEquipanteEvento);
        return response;
      },
      invalidatesTags: () => [
        { type: "Equipante" },
        { type: "EquipesMembros" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "ResumoEvento" },
      ],
    }),
    getInscricaoEquipe: build.query<number | undefined, { eventoId: number; email: string }>({
      query: ({ eventoId, email }) => ({
        url: `/equipes/inscricao/${eventoId}/${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    getMpConfigInscricaoEquipe: build.query<
      {
        publicKey: string | null;
        checkoutTransparente: boolean;
        pixDisponivel?: boolean;
        cartaoDisponivel?: boolean;
        taxaConveniencia?: number;
      },
      number
    >({
      query: (eventoId) => ({
        url: `/equipes/checkout/mp-config/${eventoId}`,
        method: "GET",
      }),
    }),
    processCheckoutTransparenteEquipe: build.mutation<
      {
        success: boolean;
        status: string;
        paymentId: string;
        statusDetail?: string | null;
        threeDsInfo?: { externalResourceUrl: string; creq: string } | null;
        pixQrCode?: string | null;
        pixCopiaECola?: string | null;
        ticketUrl?: string | null;
      },
      { equipanteEventoId: number; eventoId: number; formData: Record<string, unknown>; deviceId?: string }
    >({
      query: (body) => ({
        url: `/equipes/checkout/process-payment`,
        method: "POST",
        params: { t: obterTokenEquipanteEvento(body.equipanteEventoId) },
        body,
      }),
    }),
    createPixInscricaoEquipe: build.mutation<
      {
        success: boolean;
        status: string;
        paymentId: string;
        pixQrCode?: string | null;
        pixCopiaECola?: string | null;
        ticketUrl?: string | null;
        dateOfExpiration?: string | null;
      },
      { equipanteEventoId: number; eventoId: number; regenerar?: boolean }
    >({
      query: (body) => ({
        url: `/equipes/checkout/create-pix`,
        method: "POST",
        params: { t: obterTokenEquipanteEvento(body.equipanteEventoId) },
        body,
      }),
    }),
    getPaymentStatusEquipe: build.query<
      import("@/services/participantes.servivce").InscricaoPaymentStatusResponse,
      number | { equipanteEventoId: number; token?: string }
    >({
      query: (arg) => ({
        url: `/equipes/checkout/payment-status/${typeof arg === "number" ? arg : arg.equipanteEventoId}`,
        method: "GET",
        params: { t: typeof arg === "number" ? obterTokenEquipanteEvento(arg) : arg.token },
      }),
    }),
    getEquipanteEventoCheckout: build.query<
      {
        Id: number;
        EquipanteId?: number | null;
        acessoCompleto?: boolean;
        Email?: string;
        Nome: string;
        Apelido?: string;
        Sexo?: number;
        TipoEvento?: number | null;
        NomeConjuge?: string | null;
        EmailConjuge?: string | null;
        NomeCasal?: string | null;
        EmailCasal?: string | null;
        MercadoPagoPreferenceId?: string | null;
        StatusMontagem?: number;
        participantesadicionais?: Array<{
          AdicionalId?: number;
          eventoAdicionais?: {
            Valor?: number;
            ValorTaxa?: number;
            Categoria?: string;
            Descricao?: string;
          };
        }>;
      },
      number | { equipanteEventoId: number; token?: string }
    >({
      query: (arg) => ({
        url: `/equipes/checkout/equipante-evento/${typeof arg === "number" ? arg : arg.equipanteEventoId}`,
        method: "GET",
        params: { t: typeof arg === "number" ? obterTokenEquipanteEvento(arg) : arg.token },
      }),
      providesTags: (_result, _error, arg) => [{ type: "Equipante", id: typeof arg === "number" ? arg : arg.equipanteEventoId }],
    }),
    validarCupomCheckoutEquipe: build.mutation<
      import("@/services/participantes.servivce").CheckoutCupomPreview,
      { equipanteEventoId: number; eventoId: number; codigo: string }
    >({
      query: (body) => ({
        url: `/equipes/checkout/validar-cupom`,
        method: "POST",
        params: { t: obterTokenEquipanteEvento(body.equipanteEventoId) },
        body,
      }),
      extraOptions: skipGlobalErrorToast,
    }),
    aplicarCupomCheckoutEquipe: build.mutation<
      import("@/services/participantes.servivce").CheckoutCupomPreview,
      { equipanteEventoId: number; eventoId: number; codigo: string }
    >({
      query: (body) => ({
        url: `/equipes/checkout/aplicar-cupom`,
        method: "POST",
        params: { t: obterTokenEquipanteEvento(body.equipanteEventoId) },
        body,
      }),
      extraOptions: skipGlobalErrorToast,
    }),
    removerCupomCheckoutEquipe: build.mutation<
      import("@/services/participantes.servivce").CheckoutCupomPreview,
      { equipanteEventoId: number; eventoId: number }
    >({
      query: (body) => ({
        url: `/equipes/checkout/remover-cupom`,
        method: "POST",
        body,
      }),
      extraOptions: skipGlobalErrorToast,
    }),
    confirmarIsencaoCupomCheckoutEquipe: build.mutation<
      { success: boolean; statusMontagem: number },
      { equipanteEventoId: number; eventoId: number }
    >({
      query: (body) => ({
        url: `/equipes/checkout/confirmar-isencao`,
        method: "POST",
        params: { t: obterTokenEquipanteEvento(body.equipanteEventoId) },
        body,
      }),
    }),
    byEventoId: build.query<any, IEventoPaginatedRequest>({
      query: ({
        page,
        eventoId,
        perPage,
        sortingField,
        sortingDir,
        search,
      }) => ({
        url: `/equipes/byEventoId/${eventoId}`,
        params: {
          page,
          perPage,
          sortingDir,
          sortingField,
          search,
        },
        method: "GET",
      }),
      providesTags: ["EquipesMembros"],
    }),
    getEquipesCompletasParaImpressao: build.query<
      any,
      { eventoId: number; equipeIds?: string }
    >({
      query: ({ eventoId, equipeIds }) => ({
        url: `/equipes/impressao/${eventoId}`,
        params: equipeIds ? { equipeIds } : {},
        method: "GET",
      }),
    }),
    extractExcelByEvento: build.mutation<any, IEventoPaginatedRequest>({
      queryFn: async (
        { eventoId, filename, campos },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/equipes/byEventoId/${eventoId}`,
          params: {
            extract: "excel",
            campos,
            filename,
          },
          method: "GET",
          responseHandler: (response) => response.blob(),
        });
        return downloadFile(result, filename);
      },
    }),
    getEquipantesPaginated: build.query<any, IEquipantesDataTableRequest>({
      query: (params) => ({
        url: `/equipes/findAllEquipantesPaginated/${params.eventoId}`,
        params,
        method: "GET",
      }),
      transformResponse: (response: any, meta, arg) => {
        // Normalizar dados e adicionar hash pré-calculado
        if (response?.data) {
          // 1. Normalizar e calcular hash (hash fica em equipantes._etiquetasHash)
          const normalized = normalizeTableDataWithHash(response.data);
          
          // 2. Achatar estrutura (move tudo para raiz, incluindo _etiquetasHash)
          const flattened = flattenEquipantesData(normalized);
          
          return {
            ...response,
            data: flattened
          };
        }
        
        return response;
      },
      providesTags: (result, error, arg) => [
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "Equipantes", id: `PARTIAL-LIST-${arg.eventoId}` },
      ],
    }),
    extractExcelEquipantes: build.mutation<any, IEquipantesDataTableRequest>({
      queryFn: async (
        { eventoId, filename, equipes, campos, status, checkin, search, etiquetas },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/equipes/findAllEquipantesPaginated/${eventoId}`,
          params: {
            extract: "excel",
            campos,
            filename,
            equipes,
            status,
            checkin,
            search,
            etiquetas,
          },
          method: "GET",
          responseHandler: (response) => response.blob(),
        });
        return downloadFile(result, filename);
      },
    }),
    getListagemGeralPaginated: build.query<any, IListagemGeralPaginatedRequest>(
      {
        query: ({
          page,
          perPage,
          sortingField,
          sortingDir,
          search,
          configuracoes,
        }) => ({
          url: `/equipes/listagemGeral`,
          params: {
            page,
            perPage,
            sortingDir,
            sortingField,
            search,
            configuracoes,
          },
          method: "GET",
        }),
        providesTags: (result) => [{ type: "ListagemGeral" }],
      }
    ),
    extractExcelListagemGeral: build.mutation<
      any,
      IListagemGeralPaginatedRequest
    >({
      queryFn: async (
        { configuracoes, filename, campos },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        const result = await baseQuery({
          url: `/equipes/listagemGeral/`,
          params: {
            extract: "excel",
            campos,
            filename,
            configuracoes,
          },
          method: "GET",
          responseHandler: (response) => response.blob(),
        });
        return downloadFile(result, filename);
      },
    }),
    /** #794: conclusão pública da inscrição de voluntário (sem login, campos mínimos). */
    getEquipanteConclusao: build.query<any, { equipanteId: number; eventoId: number }>({
      query: ({ equipanteId, eventoId }) => ({
        url: `/equipes/checkout/inscricao-concluida/${equipanteId}`,
        params: { eventoId },
        method: "GET",
      }),
      providesTags: (result) => [{ type: "Equipante", id: result?.Id }],
    }),
    getEquipante: build.query<
      any,
      { equipanteId: number; eventoId: number | null | undefined }
    >({
      queryFn: async ({ equipanteId, eventoId }, api, extraOptions, baseQuery) => {
        const resolvedEventoId =
          eventoId ??
          (api.getState() as RootState).eventosReducer.EventoId;
        if (resolvedEventoId) {
          const result = await baseQuery({
            url: `/equipes/findOne/${equipanteId}`,
            params: {
              eventoId: resolvedEventoId,
            },
            method: "GET",
          });
          return result as any;
        }
        return { error: { status: 400, data: "EventoId obrigatório" } };
      },
      serializeQueryArgs: ({ queryArgs }) =>
        `${queryArgs.equipanteId}:${queryArgs.eventoId ?? "none"}`,
      providesTags: (_result, _error, arg) => [
        {
          type: "Equipante",
          id: `${arg.equipanteId}-${arg.eventoId ?? "none"}`,
        },
        { type: "Equipante" },
      ],
    }),
    getEquipanteSemEvento: build.query<
      any,
      number | { equipanteId: number; loginId?: number; eventoId?: number }
    >({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const state = api.getState() as RootState;
        const equipanteId = typeof arg === "number" ? arg : arg.equipanteId;
        const loginId =
          typeof arg === "number"
            ? state.generalReducer.LoginId
            : (arg.loginId ?? state.generalReducer.LoginId);
        const eventoId =
          typeof arg === "number"
            ? state.eventosReducer.EventoId
            : (arg.eventoId ?? state.eventosReducer.EventoId);
        const result = await baseQuery({
          url: `/equipes/findOneSemEvento/${equipanteId}`,
          method: "GET",
          params: {
            ...(loginId != null && loginId !== undefined ? { loginId } : {}),
            ...(eventoId ? { eventoId } : {}),
          },
        });
        return result;
      },
      providesTags: () => [
        {
          type: "Equipante",
        },
      ],
    }),
    getEquipanteLogin: build.query<any, any>({
      queryFn: async ({ email, loginId }, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/equipes/findByLogin`,
          params: {
            email,
            loginId,
          },
          method: "GET",
        });
        return result;
      },
      providesTags: (res, err, arg) => [
        {
          type: "EquipanteLogin",
          id: res?.LoginId,
        },
      ],
      async onQueryStarted(_query, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        dispatch(setEquipante(data));
      },
    }),
    /** Mesmo findByLogin, sem gravar o aluno no auth.equipantes. */
    getEquipanteByEmail: build.query<any, { email: string; loginId: number }>({
      queryFn: async ({ email, loginId }, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/equipes/findByLogin`,
          params: {
            email,
            loginId,
          },
          method: "GET",
        });
        return result;
      },
      providesTags: (res) => [
        {
          type: "EquipanteLogin",
          id: res?.Id,
        },
      ],
    }),
    getEquipe: build.query<any, number | { equipeId: number; eventoId?: number; modoIndividual?: boolean }>({
      queryFn: async (params, api, extraOptions, baseQuery) => {
        const equipeId = typeof params === "number" ? params : params.equipeId;
        const eventoId = typeof params === "number"
          ? (api.getState() as RootState).eventosReducer.EventoId
          : (params.eventoId ?? (api.getState() as RootState).eventosReducer.EventoId);
        const modoIndividual = typeof params === "object" && params.modoIndividual;

        const result = await baseQuery({
          url: `/equipes/findEquipe/${equipeId}`,
          params: {
            eventoId,
            ...(modoIndividual ? { modoIndividual: true } : {}),
          },
          method: "GET",
        });
        return result;
      },
      providesTags: () => [
        {
          type: "Equipe",
        },
      ],
    }),
    addEtiquetaEquipante: build.mutation<any, any>({
      query: (body) => ({
        url: `/equipes/etiquetas/add/${body.EquipanteId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, params) => {
        return [
          { type: "Equipantes", id: params.EquipanteId },
          { type: "Equipantes", id: "PARTIAL-LIST" },
          // Invalidar todas as queries paginadas para o eventoId se disponível
          ...(params.EventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${params.EventoId}` }] : []),
        ];
      },
    }),
    removeEtiquetaEquipante: build.mutation<any, any>({
      query: (body) => ({
        url: `/equipes/etiquetas/remove/${body.EquipanteId}`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: "Equipantes", id: params.EquipanteId },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        // Invalidar todas as queries paginadas para o eventoId se disponível
        ...(params.EventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${params.EventoId}` }] : []),
      ],
    }),

    etiquetasEquipantes: build.mutation<any, any>({
      query: (body) => ({
        url: `/equipes/etiquetas/${body.EquipanteId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, params) => [
        { type: "Equipantes", id: params.EquipanteId },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        // Invalidar todas as queries paginadas para o eventoId se disponível
        ...(params.EventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${params.EventoId}` }] : []),
      ],
    }),
    setAvaliacao: build.mutation<any, IAvaliacao>({
      query: (body) => {
        return {
          url: `/equipes/avaliacao`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, change) => {
        return [
          { type: "Equipante" },
          { type: "Coordenador" },
          { type: "EquipesMembros" },
          { type: "Equipantes", id: "PARTIAL-LIST" },
          // Invalidar todas as queries paginadas para o eventoId se disponível
          ...(change?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${change.eventoId}` }] : []),
        ];
      },
    }),
    changeEquipe: build.mutation<any, IChangeQuarto>({
      query: (body) => {
        return {
          url: `/equipes/changeEquipe`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, change) => {
        return [
          { type: "Equipante" },
          { type: "Coordenador" },
          { type: "EquipesMembros" },
          { type: "Historico" },
          { type: "ResumoEvento" },
          { type: "SelectAddVoluntario" },
          { type: "Equipantes", id: "PARTIAL-LIST" },
          { type: "Equipantes", id: change.participanteId },
          // Invalidar todas as queries paginadas para o eventoId se disponível
          ...(change?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${change.eventoId}` }] : []),
        ];
      },
    }),
    changeEquipePai: build.mutation<any, IChangeEquipePai>({
      query: (body) => {
        return {
          url: `/equipes/changeEquipePai`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (result, error, change) => {
        return [
          { type: "Equipes" },
          { type: "EquipesMembros" },
          {
            type: "Equipe",
          },
        ];
      },
    }),
    toggleStatusEquipanteEvento: build.mutation<
      any,
      IToggleStatusEquipanteEvento
    >({
      query: ({ equipanteEventoId, status }) => ({
        url: `/equipes/toggle/${equipanteEventoId}/${status}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Equipante" },
        { type: "ResumoEvento" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        // Invalidar todas as queries paginadas para o eventoId se disponível
        ...(arg?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${arg.eventoId}` }] : []),
      ],
    }),
    ativarVoluntario: build.mutation<any, { equipanteEventoId: number; eventoId?: number }>({
      query: ({ equipanteEventoId }) => ({
        url: `/equipes/ativar-voluntario/${equipanteEventoId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Equipante" },
        { type: "ResumoEvento" },
        { type: "Coordenador" },
        { type: "VoluntariosEvento" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        ...(arg?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${arg.eventoId}` }] : []),
      ],
    }),
    deletarVoluntario: build.mutation<void, { equipanteEventoId: number; eventoId?: number }>({
      query: ({ equipanteEventoId }) => ({
        url: `/equipes/equipante-evento/${equipanteEventoId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Equipante" },
        { type: "ResumoEvento" },
        { type: "Coordenador" },
        { type: "VoluntariosEvento" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        ...(arg?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${arg.eventoId}` }] : []),
      ],
    }),
    toggleCheckinEquipante: build.mutation<any, IEquipanteActionRequest>({
      query: ({ equipanteId, eventoId }) => ({
        url: `/equipes/toggleCheckin/${equipanteId}`,
        params: {
          eventoId,
        },
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Equipante" },
        { type: "ResumoEvento" },
        { type: "Coordenador" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "SelectParticipantes" },
        { type: "SelectCasais" },
        // Invalidar todas as queries paginadas para o eventoId se disponível
        ...(arg?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${arg.eventoId}` }] : []),
      ],
    }),
    toggleCoordenador: build.mutation<any, IEquipanteActionRequest>({
      query: ({ equipanteId, eventoId }) => ({
        url: `/equipes/toggleCoordenador/${equipanteId}`,
        params: {
          eventoId,
        },
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Equipante" },
        { type: "Historico" },
        { type: "Coordenador" },
        { type: "ResumoEvento" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "EquipesMembros" },
        { type: "Equipes" },
        { type: "EquipesEvento" },
        // Invalidar todas as queries paginadas para o eventoId se disponível
        ...(arg?.eventoId ? [{ type: "Equipantes", id: `PARTIAL-LIST-${arg.eventoId}` }] : []),
      ],
    }),
    postEquipante: build.mutation<any, any>({
      query: (body: any) => ({
        url: `/equipes/equipante/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (res, err, arg) => [
        { type: "Equipante" },
        { type: "ListagemGeral" },
        { type: "EquipesMembros" },
        { type: "Coordenador" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "Participantes", id: "LISTAGEM-GERAL" },
        { type: "Participantes", id: "LIST" },
        {
          type: "EquipanteLogin",
          id: res?.LoginId,
        },
        // Invalidar todas as queries paginadas e selectConjugeEvento para o eventoId
        ...(arg?.EventoId
          ? [
              { type: "Equipantes" as const, id: `PARTIAL-LIST-${arg.EventoId}` },
              { type: "Equipantes" as const, id: `conjuge-${arg.EventoId}` },
            ]
          : []),
      ],
    }),
    getSelectAddVoluntario: build.query<
      any,
      { search: string; eventoId: number | null | undefined }
    >({
      queryFn: async ({ search, eventoId }, api, extraOptions, baseQuery) => {
        const resolvedEventoId =
          eventoId ?? (api.getState() as RootState).eventosReducer.EventoId;
        const result = await baseQuery({
          url: `/equipes/selectVoluntario`,
          params: {
            search,
            eventoId: resolvedEventoId,
          },
          method: "GET",
          responseHandler: (response) => response.json(),
        });
        return result;
      },
      serializeQueryArgs: ({ queryArgs }) =>
        `${queryArgs.eventoId ?? "none"}:${queryArgs.search}`,
      providesTags: (_result, _error, arg) => [
        { type: "SelectAddVoluntario", id: String(arg.eventoId ?? "none") },
        { type: "SelectAddVoluntario" },
      ],
    }),
    getSugestaoConjuge: build.query<
      SugestaoConjuge | null,
      { eventoId: number; pessoaId: number; tipoPessoa: number }
    >({
      query: ({ eventoId, pessoaId, tipoPessoa }) => ({
        url: `/equipes/sugerir-conjuge`,
        params: { eventoId, pessoaId, tipoPessoa },
        method: "GET",
      }),
      extraOptions: skipGlobalErrorToast,
      keepUnusedDataFor: 30,
    }),
    getSelectConjugeEvento: build.query<
      { Id: number; Nome: string; Apelido: string; Email: string; Sexo?: number }[],
      { eventoId: number; excludeEquipanteId?: number; search?: string }
    >({
      query: ({ eventoId, excludeEquipanteId, search }) => ({
        url: `/equipes/selectConjugeEvento/${eventoId}`,
        params: { excludeEquipanteId, search },
        method: "GET",
      }),
      providesTags: (_result, _err, arg) => [
        { type: "Equipantes", id: `conjuge-${arg.eventoId}` },
      ],
    }),
    getSelectAddVoluntarioSemEvento: build.query<any, string>({
      queryFn: async (search, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/equipes/selectVoluntario`,
          params: {
            search,
          },
          method: "GET",
          responseHandler: (response) => response.json(),
        });
        return result;
      },
      providesTags: [{ type: "SelectAddVoluntario" }],
    }),
    getHistorico: build.query<any, string>({
      queryFn: async (email, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/equipes/historico`,
          params: {
            email,
          },
          method: "GET",
          responseHandler: (response) => response.json(),
        });
        return result;
      },
      providesTags: [{ type: "Historico" }],
    }),
    getHistoricoResumo: build.query<
      import("@/interfaces/historico.interface").HistoricoResumo,
      { email: string; loginId?: number; equipanteId?: number }
    >({
      query: (params) => ({
        url: `/equipes/historico/resumo`,
        params,
        method: "GET",
      }),
      providesTags: [{ type: "Historico", id: "RESUMO" }],
    }),
    getHistoricoItens: build.query<
      import("@/interfaces/historico.interface").HistoricoItensPaginated,
      {
        email: string;
        modulo: "eventos" | "pequenos-grupos" | "ensino" | "escalas" | "ministerios";
        papel: import("@/interfaces/historico.interface").HistoricoPapelQuery;
        page?: number;
        perPage?: number;
        loginId?: number;
        equipanteId?: number;
      }
    >({
      query: ({ page = 1, perPage = 15, ...rest }) => ({
        url: `/equipes/historico/itens`,
        params: { ...rest, page, perPage },
        method: "GET",
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { email, modulo, papel, loginId, perPage, equipanteId } = queryArgs;
        return { email, modulo, papel, loginId, perPage, equipanteId };
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }
        const existingKeys = new Set(
          currentCache.data.map((item) =>
            `${item.type}-${item.EventoId}-${item.date}`,
          ),
        );
        const newUnique = newItems.data.filter(
          (item) =>
            !existingKeys.has(`${item.type}-${item.EventoId}-${item.date}`),
        );
        return {
          ...newItems,
          data: [...currentCache.data, ...newUnique],
        };
      },
      forceRefetch({ currentArg, previousArg }) {
        if (!previousArg) return true;
        return (
          currentArg?.page !== previousArg?.page ||
          currentArg?.papel !== previousArg?.papel ||
          currentArg?.modulo !== previousArg?.modulo ||
          currentArg?.email !== previousArg?.email
        );
      },
      providesTags: [{ type: "Historico", id: "ITENS" }],
    }),
    getHistoricoBreakdown: build.query<
      import("@/interfaces/historico.interface").HistoricoBreakdown,
      {
        email: string;
        modulo: "eventos" | "escalas";
        loginId?: number;
      }
    >({
      query: (params) => ({
        url: `/equipes/historico/breakdown`,
        params,
        method: "GET",
      }),
      providesTags: [{ type: "Historico", id: "BREAKDOWN" }],
    }),
    removerDuplicado: build.mutation<any, IRemoverDuplicadoDto>({
      query: (body) => ({
        url: `/equipes/removerDuplicado`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "Equipante" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "SelectAddVoluntario" },
        { type: "ListagemGeral" }, // Invalidar listagem geral após remover duplicado
      ],
    }),
    montagemAssistenteContexto: build.query<
      any,
      {
        eventoId: number;
        loginId: number;
        incluirEventoAtual?: boolean;
        historicoApenasConfirmados?: boolean;
        incluirStatusMontagem17?: boolean;
        eventosHistoricoIds?: number[];
        eventosOrigemIds?: number[];
        eventosNuncaServiramIds?: number[];
      }
    >({
      query: (params) => ({
        url: `/equipes/montagem-assistente/contexto`,
        params: {
          eventoId: params.eventoId,
          loginId: params.loginId,
          incluirEventoAtual: params.incluirEventoAtual !== false,
          historicoApenasConfirmados: params.historicoApenasConfirmados !== false,
          incluirStatusMontagem17: params.incluirStatusMontagem17 === true,
          eventosHistoricoIds: params.eventosHistoricoIds?.length
            ? params.eventosHistoricoIds.join(",")
            : undefined,
          eventosOrigemIds: params.eventosOrigemIds?.length
            ? params.eventosOrigemIds.join(",")
            : undefined,
          eventosNuncaServiramIds: params.eventosNuncaServiramIds?.length
            ? params.eventosNuncaServiramIds.join(",")
            : undefined,
        },
      }),
    }),
    getMontagemAssistenteBuscaVoluntario: build.query<
      MontagemAssistenteBuscaVoluntarioItem[],
      string
    >({
      queryFn: async (search, api, extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/equipes/montagem-assistente/busca-voluntario`,
          params: {
            search,
            eventoId: (api.getState() as RootState).eventosReducer.EventoId,
          },
          method: "GET",
          priority: "high",
          responseHandler: (response) => response.json(),
        });
        return result as QueryReturnValue<
          MontagemAssistenteBuscaVoluntarioItem[],
          FetchBaseQueryError,
          object
        >;
      },
    }),
    montagemAssistenteSugerir: build.mutation<any, any>({
      query: (body) => ({
        url: `/equipes/montagem-assistente/sugerir`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [
        { type: "Equipes" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
      ],
    }),
    aprovarMontagem: build.mutation<
      { criados: number; inseridos: number; atualizados: number },
      {
        eventoId: number;
        loginId: number;
        itens: {
          equipanteId?: number;
          participanteId?: number;
          equipeId: number;
          coordenador?: boolean;
        }[];
      }
    >({
      query: ({ loginId, ...body }) => ({
        url: `/equipes/montagem-assistente/aprovar`,
        method: "POST",
        params: { loginId },
        body,
      }),
      invalidatesTags: () => [
        { type: "Equipes" },
        { type: "Equipantes", id: "PARTIAL-LIST" },
        { type: "ListagemGeral" },
      ],
    }),
    validarCasaisMontagem: build.mutation<
      { paresInvalidos: ParInvalidoMontagem[] },
      {
        eventoId: number;
        loginId: number;
        itens: {
          equipanteId?: number;
          participanteId?: number;
          equipeId: number;
          coordenador?: boolean;
        }[];
      }
    >({
      query: ({ loginId, ...body }) => ({
        url: `/equipes/montagem-assistente/validar-casais`,
        method: "POST",
        params: { loginId },
        body,
      }),
    }),
    definirMaridosMontagem: build.mutation<
      { atualizados: number },
      { eventoId: number; loginId: number; pares: { maridoId: number; esposaId: number }[] }
    >({
      query: ({ loginId, ...body }) => ({
        url: `/equipes/montagem-assistente/definir-maridos`,
        method: "POST",
        params: { loginId },
        body,
      }),
      invalidatesTags: () => [{ type: "Equipantes", id: "PARTIAL-LIST" }],
    }),
    montagemAssistenteEventosOrigem: build.query<
      { id: number; titulo: string; dataEvento: string | null }[],
      {
        eventoId: number;
        loginId: number;
        search?: string;
        page?: number;
        includeIds?: string;
      }
    >({
      query: ({ eventoId, loginId, search, page, includeIds }) => ({
        url: `/equipes/montagem-assistente/eventos-origem`,
        params: {
          eventoId,
          loginId,
          ...(search ? { search } : {}),
          ...(page != null ? { page } : {}),
          ...(includeIds ? { includeIds } : {}),
        },
      }),
    }),
    montagemAssistenteRascunho: build.query<
      {
        eventoId: number;
        dataEdicao: string | null;
        payload: import("@/interfaces/montagem-equipes.interface").MontagemAssistenteRascunhoPayload;
      } | null,
      { eventoId: number; loginId: number }
    >({
      query: ({ eventoId, loginId }) => ({
        url: `/equipes/montagem-assistente/rascunho`,
        params: { eventoId, loginId },
      }),
      providesTags: (_result, _error, arg) => [
        { type: "MontagemAssistenteRascunho", id: arg.eventoId },
      ],
    }),
    salvarMontagemAssistenteRascunho: build.mutation<
      {
        eventoId: number;
        dataEdicao: string | null;
        payload: import("@/interfaces/montagem-equipes.interface").MontagemAssistenteRascunhoPayload;
      },
      {
        eventoId: number;
        loginId: number;
        payload: import("@/interfaces/montagem-equipes.interface").MontagemAssistenteRascunhoPayload;
      }
    >({
      query: ({ loginId, eventoId, payload }) => ({
        url: `/equipes/montagem-assistente/rascunho`,
        method: "POST",
        params: { loginId },
        body: { eventoId, payload },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "MontagemAssistenteRascunho", id: arg.eventoId },
      ],
    }),
    excluirMontagemAssistenteRascunho: build.mutation<
      { ok: boolean },
      { eventoId: number; loginId: number }
    >({
      query: ({ eventoId, loginId }) => ({
        url: `/equipes/montagem-assistente/rascunho`,
        method: "DELETE",
        params: { eventoId, loginId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "MontagemAssistenteRascunho", id: arg.eventoId },
      ],
    }),
    getEquipeEventosVinculados: build.query<
      {
        eventos: Array<{
          Id: number;
          Titulo: string;
        }>;
        total: number;
      },
      { equipeId: number; eventoIdAtual?: number }
    >({
      query: ({ equipeId, eventoIdAtual }) => ({
        url: `/equipes/${equipeId}/eventos-vinculados`,
        params: eventoIdAtual ? { eventoIdAtual } : undefined,
        method: "GET",
      }),
    }),
  }),
});

export const equipesApiEndpoints = getBaseApiEndpointObjects(equipesAPI, RESOURCES.EQUIPES);

export const {
  useGetEquipesQuery,
  usePostEquipeMutation,
  usePostInscricaoEquipeMutation,
  useGetInscricaoEquipeQuery,
  useGetMpConfigInscricaoEquipeQuery,
  useProcessCheckoutTransparenteEquipeMutation,
  useCreatePixInscricaoEquipeMutation,
  useGetPaymentStatusEquipeQuery,
  useGetEquipanteEventoCheckoutQuery,
  useValidarCupomCheckoutEquipeMutation,
  useAplicarCupomCheckoutEquipeMutation,
  useRemoverCupomCheckoutEquipeMutation,
  useConfirmarIsencaoCupomCheckoutEquipeMutation,
  useGetEquipanteQuery,
  useGetEquipanteConclusaoQuery,
  useGetHistoricoQuery,
  useGetHistoricoResumoQuery,
  useGetHistoricoItensQuery,
  useLazyGetHistoricoItensQuery,
  useGetHistoricoBreakdownQuery,
  useExtractExcelEquipantesMutation,
  useGetEquipantesPaginatedQuery,
  useByEventoIdQuery,
  useEtiquetasEquipantesMutation,
  useChangeEquipeMutation,
  useAtivarVoluntarioMutation,
  useDeletarVoluntarioMutation,
  usePostEquipanteMutation,
  useGetSelectAddVoluntarioQuery,
  useLazyGetEquipanteQuery,
  useGetEquipanteSemEventoQuery,
  useExtractExcelByEventoMutation,
  useGetEquipeQuery,
  useAddEtiquetaEquipanteMutation,
  useRemoveEtiquetaEquipanteMutation,
  useExtractExcelListagemGeralMutation,
  useGetListagemGeralPaginatedQuery,
  useGetSelectAddVoluntarioSemEventoQuery,
  useGetSelectConjugeEventoQuery,
  useLazyGetSugestaoConjugeQuery,
  useRemoverDuplicadoMutation,
  useGetEquipesCompletasParaImpressaoQuery,
  useMontagemAssistenteContextoQuery,
  useLazyMontagemAssistenteContextoQuery,
  useGetMontagemAssistenteBuscaVoluntarioQuery,
  useMontagemAssistenteSugerirMutation,
  useAprovarMontagemMutation,
  useValidarCasaisMontagemMutation,
  useDefinirMaridosMontagemMutation,
  useMontagemAssistenteEventosOrigemQuery,
  useLazyMontagemAssistenteEventosOrigemQuery,
  useMontagemAssistenteRascunhoQuery,
  useLazyMontagemAssistenteRascunhoQuery,
  useSalvarMontagemAssistenteRascunhoMutation,
  useExcluirMontagemAssistenteRascunhoMutation,
  useToggleStatusEquipanteEventoMutation,
  useLazyGetEquipeEventosVinculadosQuery,
} = equipesAPI;
